/**
 * One-time data integrity cleanup that runs on every app startup.
 * Scans Dexie tables for records with invalid primary keys (undefined, null,
 * empty string, or non-string) and removes them. Idempotent — no-op when
 * all records are valid.
 */

import { db } from './storage/db';

// Minimal interface for a Dexie table with id-keyed records
interface CleanableTable {
  toArray(): Promise<Array<Record<string, unknown>>>;
  filter(fn: (record: Record<string, unknown>) => boolean): { delete(): Promise<number> };
}

function isInvalidId(id: unknown): boolean {
  return !id || typeof id !== 'string';
}

async function cleanTable(
  // CAST: each typed Dexie table is cast to this minimal interface for uniform cleanup
  table: CleanableTable,
  tableName: string,
): Promise<void> {
  try {
    const records = await table.toArray();
    const badRecords = records.filter((r) => isInvalidId(r['id']));
    if (badRecords.length === 0) return;

    console.warn(
      `[data-cleanup] Found ${badRecords.length} record(s) with invalid id in table: ${tableName}`,
      badRecords,
    );
    const deleted = await table.filter((r) => isInvalidId(r['id'])).delete();
    console.info(`[data-cleanup] Deleted ${deleted} invalid record(s) from table: ${tableName}`);
  } catch (err) {
    console.error(`[data-cleanup] Error scanning table ${tableName}:`, err);
  }
}

export async function cleanupInvalidRecords(): Promise<void> {
  const tables: Array<[CleanableTable, string]> = [
    // CAST: typed Dexie tables cast to CleanableTable for heterogeneous iteration
    [db.users as unknown as CleanableTable, 'users'],
    [db.userPreferences as unknown as CleanableTable, 'userPreferences'],
    [db.properties as unknown as CleanableTable, 'properties'],
    [db.locations as unknown as CleanableTable, 'locations'],
    [db.seasons as unknown as CleanableTable, 'seasons'],
    [db.tasks as unknown as CleanableTable, 'tasks'],
    [db.logEntries as unknown as CleanableTable, 'logEntries'],
    [db.photos as unknown as CleanableTable, 'photos'],
    [db.shoppingListItems as unknown as CleanableTable, 'shoppingListItems'],
  ];

  for (const [table, name] of tables) {
    await cleanTable(table, name);
  }
}
